// Copyright (c) Ping. All rights reserved.
//
// The settings walkthrough: one stop per pane, in sidebar order. Each step
// opens the pane (by clicking its sidebar tab) and then spotlights the
// floating guide button inside it, so the user learns what every pane is for
// and that a hands-on guide is one tap away. Clicking that button launches the
// detailed per-pane guide (see PaneGuideHost). Runs the first time the settings
// modal opens.

import { SETTINGS_TAB_ORDER, type SettingsTabId } from '@ping/ui/src/settings-modal/types';

import type { GuidedStep } from './GuidedTourTypes';

// One-line description of what each pane is for. The step appends a shared
// pointer to the guide button.
const PANE_SUMMARY: Record<SettingsTabId, { title: string; body: string }> = {
  account: {
    title: 'Account',
    body: 'Your profile, display name, and how you show up when people search.',
  },
  session: {
    title: 'Session',
    body: 'See where you are signed in and keep your account secure.',
  },
  appearance: {
    title: 'Appearance',
    body: 'Switch between light and dark, and choose your chat background.',
  },
  notifications: {
    title: 'Notifications',
    body: 'Decide what you get notified about, and how.',
  },
  preferences: {
    title: 'Preferences',
    body: 'The small touches, like press Enter to send and auto downloads.',
  },
  keyboard: {
    title: 'Keyboard',
    body: 'Every shortcut for getting around Ping faster.',
  },
  sidebar: {
    title: 'Sidebar',
    body: 'Pick which tools sit in your sidebar, and in what order.',
  },
  voiceVideo: {
    title: 'Voice and video',
    body: 'Set the camera and microphone Ping uses for your calls.',
  },
  storage: {
    title: 'Storage',
    body: 'See what Ping is using and clear cached media when you need space.',
  },
  securityPrivacy: {
    title: 'Security and privacy',
    body: 'Control read receipts, typing indicators, and who can reach you.',
  },
  encryption: {
    title: 'Encryption',
    body: 'Your chats are end to end encrypted. Review the devices on your account here.',
  },
  linking: {
    title: 'Linked devices',
    body: 'Add Ping on another device by scanning a code.',
  },
  recovery: {
    title: 'Account recovery',
    body: 'Save your recovery phrase so you never lose access to your account.',
  },
  helpAbout: {
    title: 'Help and about',
    body: 'Reach support and learn more about Ping.',
  },
};

function selectPane(tab: SettingsTabId): () => void {
  return () => {
    if (typeof document === 'undefined') {
      return;
    }
    const el = document.querySelector<HTMLElement>(`[data-testid="settings-tab-${tab}"]`);
    // RN-Web renders the tab as a clickable element; a synthetic click runs
    // its onPress and switches the pane behind the spotlight.
    el?.click();
  };
}

export function buildSettingsTourSteps(): GuidedStep[] {
  return SETTINGS_TAB_ORDER.map((tab) => {
    const summary = PANE_SUMMARY[tab];
    return {
      id: `settings-${tab}`,
      // The floating guide button is a fixed element present in every pane, so
      // the spotlight lands on it while the pane content changes behind.
      selector: '[data-testid="settings-pane-guide"]',
      title: summary.title,
      body: `${summary.body} Tap this button for a full walkthrough of this section.`,
      position: 'left',
      onEnter: selectPane(tab),
    };
  });
}
